import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'

import { useArabicSessionLanguage } from '../global/context/SessionSettingsContext'
import { Direction } from '../ui/common/types'
import { toSentenceCase } from '../utils/format.utils'

interface UsePopupPositionParams {
  boxRef: React.RefObject<HTMLDivElement>
  contentDirection: Direction
  isOpen: boolean
  triggerRef: React.RefObject<HTMLDivElement>
  distance?: string
}

interface UsePopupPosition {
  contentOrigin: Positions | null
}

export type Positions = {
  top: number
  left: number
  bottom?: number
  transform?: string
}

export type Rect = {
  top: number
  left: number
  width: number
  height: number
  bottom: number
  right: number
}

interface PositionToSet {
  horizontal: 'left' | 'right' | ''
  vertical: 'top' | 'bottom' | ''
}

const getHorizontal = (direction?: Direction) => {
  if (direction?.toLowerCase().includes('left')) {
    return 'left'
  }
  if (direction?.toLowerCase().includes('right')) {
    return 'right'
  }
  return ''
}

const getVertical = (direction?: Direction) => {
  if (direction?.includes('top')) {
    return 'top'
  }
  if (direction?.includes('bottom')) {
    return 'bottom'
  }
  return ''
}

interface PopupPositions {
  right: number
  left: number
  top: number
  bottom: number
  width: number
  height: number
}

const calculatePositions = (
  boxRef: React.RefObject<HTMLDivElement>,
  screenWidth: number,
  screenHeight: number,
  isArabic: boolean
): PopupPositions => {
  const rect = boxRef.current?.getBoundingClientRect() ?? {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: 0,
    height: 0,
  }
  const rightPosition = screenWidth - rect.right
  const leftPosition = rect.left

  return {
    right: isArabic ? leftPosition : rightPosition,
    left: isArabic ? rightPosition : leftPosition,
    top: rect.top,
    bottom: screenHeight - rect.bottom,
    width: rect.width,
    height: rect.height,
  }
}

const fitsRight = (
  positionToSet: PositionToSet,
  popupPositions: PopupPositions,
  triggerWidth: number,
  threshold: number
): boolean => {
  if (positionToSet.horizontal === 'right') {
    return popupPositions.right - threshold > 0
  }

  if (positionToSet.horizontal === 'left') {
    return popupPositions.right - triggerWidth - threshold - popupPositions.width > 0
  }

  if (positionToSet.horizontal === '') {
    return popupPositions.right - triggerWidth - threshold - popupPositions.width / 2 > 0
  }

  return false
}

const fitsLeft = (
  positionToSet: PositionToSet,
  popupPositions: PopupPositions,
  triggerWidth: number,
  threshold: number
): boolean => {
  if (positionToSet.horizontal === 'left') {
    return popupPositions.left - threshold > 0
  }

  if (positionToSet.horizontal === 'right') {
    return popupPositions.left - triggerWidth - threshold - popupPositions.width > 0
  }

  if (positionToSet.horizontal === '') {
    return popupPositions.left - triggerWidth - threshold - popupPositions.width / 2 > 0
  }

  return false
}

export const usePopupPosition = ({
  boxRef,
  contentDirection,
  isOpen,
  triggerRef,
  distance = '1.6rem',
}: UsePopupPositionParams): UsePopupPosition => {
  const [triggerElementRect, setTriggerElementRect] = useState<Rect | null>(null)
  const [newDirection, setNewDirection] = useState<Direction>(contentDirection)
  const isArabic = useArabicSessionLanguage()
  const initialScrollRef = useRef({ top: window.scrollY, left: window.scrollX })
  const [scrollDelta, setScrollDelta] = useState({ top: 0, left: 0 })

  const handlePopupChange = useCallback(() => {
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const threshold = 50

    const expectedVertical = getVertical(newDirection)
    const expectedHorizontal = getHorizontal(newDirection)

    const popupPositions = calculatePositions(boxRef, screenWidth, screenHeight, isArabic)

    const positionToSet: PositionToSet = {
      horizontal: expectedHorizontal,
      vertical: expectedVertical,
    }

    if (popupPositions.top < threshold) {
      positionToSet.vertical = 'bottom'
    } else if (popupPositions.bottom < threshold) {
      positionToSet.vertical = 'top'
    }

    const triggerRect = triggerRef.current?.getBoundingClientRect()
    const triggerHeight = triggerRect?.height ?? 0
    const triggerWidth = triggerRect?.width ?? 0
    const doesFitRight = fitsRight(positionToSet, popupPositions, triggerWidth, threshold)
    const doesFitLeft = fitsLeft(positionToSet, popupPositions, triggerWidth, threshold)

    if (
      popupPositions.top < popupPositions.height + triggerHeight &&
      popupPositions.bottom < popupPositions.height + triggerHeight
    ) {
      positionToSet.vertical = ''

      if (doesFitRight) {
        positionToSet.horizontal = 'right'
      } else if (doesFitLeft) {
        positionToSet.horizontal = 'left'
      }
    }

    if (doesFitRight) {
      positionToSet.horizontal = 'right'
    } else if (doesFitLeft) {
      positionToSet.horizontal = 'left'
    } else if (!doesFitRight && !doesFitLeft) {
      positionToSet.horizontal = ''

      if (popupPositions.top > popupPositions.bottom && popupPositions.top < threshold) {
        positionToSet.vertical = 'top'
      } else {
        positionToSet.vertical = 'bottom'
      }
    }

    const updatedDirection = positionToSet.vertical
      ? (`${positionToSet.vertical}${
          positionToSet.horizontal ? toSentenceCase(positionToSet.horizontal) : ''
        }` as Direction)
      : (positionToSet.horizontal as Direction)

    setNewDirection(updatedDirection)
  }, [boxRef, newDirection, isArabic, triggerRef])

  useEffect(() => {
    if (!isOpen) {
      setNewDirection(contentDirection)
      setTriggerElementRect(null)
      setScrollDelta({ top: 0, left: 0 })
    }
  }, [isOpen, contentDirection])

  const checkPosition = useCallback((): Rect | null => {
    const triggerRefRect = triggerRef.current?.getBoundingClientRect()

    if (!triggerRefRect) {
      return null
    }

    const rect: Rect = {
      top: triggerRefRect.top,
      left: triggerRefRect.left,
      width: triggerRefRect.width,
      height: triggerRefRect.height,
      bottom: triggerRefRect.bottom,
      right: triggerRefRect.right,
    }

    if (
      triggerRefRect?.top !== triggerElementRect?.top ||
      triggerRefRect?.left !== triggerElementRect?.left
    ) {
      setTriggerElementRect(rect)
    }

    return rect
  }, [triggerElementRect, triggerRef])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    initialScrollRef.current = { top: window.scrollY, left: window.scrollX }

    const handleScroll = () => {
      const currentScroll = { top: window.scrollY, left: window.scrollX }
      setScrollDelta({
        top: currentScroll.top - initialScrollRef.current.top,
        left: currentScroll.left - initialScrollRef.current.left,
      })
    }

    const debouncedRecalc = debounce(() => {
      checkPosition()
      handlePopupChange()
      setScrollDelta({ top: 0, left: 0 })
      initialScrollRef.current = { top: window.scrollY, left: window.scrollX }
    }, 150)

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)
    window.addEventListener('scroll', debouncedRecalc)
    window.addEventListener('resize', debouncedRecalc)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      window.removeEventListener('scroll', debouncedRecalc)
      window.removeEventListener('resize', debouncedRecalc)
      debouncedRecalc.cancel()
    }
  }, [isOpen, checkPosition, handlePopupChange])

  const contentOrigin = useMemo<Positions | null>(() => {
    if (!triggerElementRect || !isOpen) {
      return null
    }
    let origin: Positions | null = null
    switch (newDirection) {
      case 'top':
        origin = {
          top: triggerElementRect.top,
          left: triggerElementRect.left + triggerElementRect.width / 2,
          transform: `translate(-50%, calc(-100% - ${distance}))`,
        }
        break
      case 'bottom':
        origin = {
          top: triggerElementRect.bottom,
          left: triggerElementRect.left + triggerElementRect.width / 2,
          transform: `translate(-50%, ${distance})`,
        }
        break
      case 'left':
        origin = {
          top: triggerElementRect.top + triggerElementRect.height / 2,
          left: triggerElementRect.left,
          transform: `translate(calc(-100% - ${distance}), -50%)`,
        }
        break
      case 'right':
        origin = {
          top: triggerElementRect.top + triggerElementRect.height / 2,
          left: triggerElementRect.right,
          transform: `translate(${distance}, -50%)`,
        }
        break
      case 'topRight':
        origin = {
          top: triggerElementRect.top,
          left: triggerElementRect.left,
          transform: `translate(0, calc(-100% - ${distance}))`,
        }
        break
      case 'topLeft':
        origin = {
          top: triggerElementRect.top,
          left: triggerElementRect.right,
          transform: `translate(-100%, calc(-100% - ${distance}))`,
        }
        break
      case 'bottomRight':
        origin = {
          top: triggerElementRect.bottom,
          left: triggerElementRect.left,
          transform: `translate(0, ${distance})`,
        }
        break
      case 'bottomLeft':
        origin = {
          top: triggerElementRect.bottom,
          left: triggerElementRect.right,
          transform: `translate(-100%, ${distance})`,
        }
        break
      default:
        origin = null
    }
    if (origin) {
      return {
        ...origin,
        top: origin.top - scrollDelta.top,
        left: origin.left - scrollDelta.left,
      }
    }
    return origin
  }, [newDirection, triggerElementRect, isOpen, distance, scrollDelta])

  useEffect(() => {
    const observer = new MutationObserver(() => {
      checkPosition()
    })

    observer.observe(triggerRef.current?.parentElement || document, {
      childList: true,
      subtree: true,
    })

    checkPosition()

    return () => {
      observer.disconnect()
    }
  }, [checkPosition, triggerRef])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    checkPosition()
    handlePopupChange()
  }, [isOpen, handlePopupChange, checkPosition])

  return {
    contentOrigin,
  }
}
